<template>
  <div class="wb2">
    <header class="wb2-top">
      <div class="wb2-brand">
        <span class="wb2-mark">D</span>
        <span>Workbench</span>
      </div>
      <div class="wb2-search">
        <input
          v-model="searchInput"
          type="search"
          placeholder="Search client, policy, TR-, or FUSE-"
          @keyup.enter="runSearch"
        />
        <button type="button" class="wb2-btn-s" :disabled="loading" @click="runSearch">Search</button>
      </div>
      <div class="wb2-top-actions">
        <button type="button" class="wb2-btn-s" @click="bookToolsOpen = !bookToolsOpen">Book tools</button>
        <router-link class="wb2-btn-s" to="/workbench">Legacy</router-link>
      </div>
    </header>

    <div v-if="bookToolsOpen" class="wb2-drawer-mask" @click.self="bookToolsOpen = false">
      <aside class="wb2-drawer">
        <h3>Book tools</h3>
        <p>Secondary tools. They do not replace the work list.</p>
        <router-link class="wb2-tool" to="/workbench?lane=maintenance" @click.native="bookToolsOpen = false">
          Policy maintenance / bordereau
        </router-link>
        <router-link class="wb2-tool" to="/workbench?lane=brokers" @click.native="bookToolsOpen = false">
          Producing brokers
        </router-link>
        <router-link class="wb2-tool" to="/workbench" @click.native="bookToolsOpen = false">
          Open legacy workbench
        </router-link>
      </aside>
    </div>

    <div class="wb2-body">
      <main class="wb2-main">
        <div class="wb2-head">
          <div>
            <span class="wb2-title">Work to do</span>
            <span class="wb2-sub">{{ subtitle }}</span>
          </div>
          <button type="button" class="wb2-btn-s" :disabled="loading" @click="loadWork">Refresh</button>
        </div>

        <div class="wb2-filters">
          <button
            v-for="f in filters"
            :key="f.key"
            type="button"
            class="wb2-chip"
            :class="{ on: nextStepFilter === f.key }"
            @click="setFilter(f.key)"
          >
            {{ f.label }}
          </button>
        </div>

        <div v-if="error" class="wb2-error">{{ error }}</div>
        <div v-else-if="loading" class="wb2-empty">Loading work…</div>
        <div v-else-if="!rows.length" class="wb2-empty">
          <h2>Nothing in this filter</h2>
          <p>Search above, or clear filters to see open work.</p>
        </div>
        <template v-else>
          <div class="wb2-cols">
            <div>Client / policy</div>
            <div>Period</div>
            <div>Position</div>
            <div>Money</div>
            <div>Next step</div>
            <div>Updated</div>
          </div>
          <button
            v-for="row in rows"
            :key="row.id"
            type="button"
            class="wb2-row"
            :class="{ sel: selected && selected.id === row.id }"
            @click="selected = row"
          >
            <div>
              <div class="n">{{ row.client || '—' }}</div>
              <div class="m">{{ rowLine(row) }}</div>
            </div>
            <div class="mono">{{ formatDate(row.periodStart) }}</div>
            <div>{{ row.positionLabel || '—' }}</div>
            <div class="mono">{{ formatMoney(row.premium) }}</div>
            <div>
              <span class="wb2-pill" :class="pillClass(row.nextStepKey)">{{ row.nextStepLabel }}</span>
            </div>
            <div class="mono m">{{ formatShortDate(row.updatedAt || row.periodStart) }}</div>
          </button>
        </template>
      </main>

      <aside v-if="selected" class="wb2-detail">
        <div class="wb2-d-head">
          <div class="label">Selected work</div>
          <div class="d-name">{{ selected.client || 'Work item' }}</div>
          <div class="mono m">{{ detailRef(selected) }}</div>
        </div>
        <div class="wb2-facts">
          <div class="fact"><span class="k">Outcome / status</span><span class="v">{{ selected.positionLabel }}</span></div>
          <div class="fact"><span class="k">Period</span><span class="v">{{ periodRange(selected) }}</span></div>
          <div class="fact"><span class="k">Billing ticket</span><span class="v">{{ selected.ledgerRowId || 'None' }}</span></div>
          <div class="fact"><span class="k">Brand</span><span class="v">{{ selected.brand || '—' }}</span></div>
          <div class="fact"><span class="k">Next step</span><span class="v">{{ selected.nextStepLabel }}</span></div>
        </div>

        <div class="wb2-steps">
          <div class="label">Billing steps</div>
          <ol>
            <li
              v-for="step in billingSteps"
              :key="step.key"
              :class="{ done: step.done, current: step.current }"
            >
              <span class="mark">{{ step.done ? '✓' : step.n }}</span>
              <span>{{ step.label }}</span>
            </li>
          </ol>
        </div>

        <div
          v-if="actionNote"
          class="wb2-note"
          :class="{
            ok:
              selected.nextStepKey === 'prepare-billing' ||
              selected.nextStepKey === 'review-and-post' ||
              selected.nextStepKey === 'process-manual-ibais'
          }"
        >
          {{ actionNote }}
        </div>
        <div v-if="brokerConnection" class="wb2-conn">
          <div class="label">Connected producing broker</div>
          <div class="leg">
            Zoho:
            <a v-if="brokerConnection.zoho && brokerConnection.zoho.url" :href="brokerConnection.zoho.url" target="_blank" rel="noopener">
              {{ brokerConnection.zoho.name || brokerConnection.zoho.id }}
            </a>
            <span v-else-if="brokerConnection.zoho">{{ brokerConnection.zoho.name || brokerConnection.zoho.id }}</span>
            <span v-else class="miss">missing</span>
          </div>
          <div class="leg">
            INSIGHT:
            <a v-if="brokerConnection.insight && brokerConnection.insight.url" :href="brokerConnection.insight.url" target="_blank" rel="noopener">
              {{ brokerConnection.insight.id }}
            </a>
            <span v-else-if="brokerConnection.insight">{{ brokerConnection.insight.id }}</span>
            <span v-else class="miss">missing</span>
          </div>
          <div class="leg">
            Neon:
            <span v-if="brokerConnection.neon">{{ brokerConnection.neon.partyRef }}</span>
            <span v-else class="miss">missing</span>
          </div>
          <button
            v-if="brokerEnsureGoldVisible"
            type="button"
            class="wb2-btn-s"
            :disabled="actionBusy"
            @click="doEnsureBrokerGold"
          >
            Ensure Gold party
          </button>
        </div>
        <div v-if="brokerStatus && brokerStatus.subagentName" class="wb2-broker-line">
          Zoho broker: <strong>{{ brokerStatus.subagentName }}</strong>
          <span v-if="brokerStatus.autoMatch"> · match: {{ brokerStatus.autoMatch.name }}</span>
        </div>
        <div v-if="showBrokerPicker" class="wb2-broker-pick">
          <input
            v-model="brokerSearch"
            type="search"
            placeholder="Search INSIGHT producing broker"
            @keyup.enter="runBrokerSearch"
          />
          <button type="button" class="wb2-btn-s" :disabled="actionBusy" @click="runBrokerSearch">Find</button>
          <button
            v-for="c in brokerCandidates"
            :key="c.id || c.name"
            type="button"
            class="wb2-candidate"
            :class="{ on: pickedBrokerId && String(pickedBrokerId) === String(c.id) }"
            :disabled="!c.id"
            @click="pickedBrokerId = c.id"
          >
            {{ c.name }}{{ c.id ? '' : ' (name only)' }}
          </button>
        </div>
        <div v-if="actionError" class="wb2-error">{{ actionError }}</div>
        <div class="wb2-actions">
          <button
            v-if="primaryAction"
            type="button"
            class="wb2-btn-p"
            :disabled="actionBusy || primaryDisabled"
            @click="runPrimary"
          >
            {{ primaryAction.label }}
          </button>
          <button
            v-if="selected.clientId"
            type="button"
            class="wb2-btn-s"
            @click="openClient(selected.clientId)"
          >
            Open client (new tab)
          </button>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import {
  getWorkbenchWork,
  createBillingWork,
  getBrokerLinkStatus,
  linkClientBroker,
  ensureBrokerGold,
  ensureClientGold,
  fixClientLink,
  searchInsightProducingBrokers
} from "@/services/workbench-service";

const FILTERS = [
  { key: "all-open", label: "All open" },
  { key: "link-broker", label: "Link broker" },
  { key: "link-for-billing", label: "Link for billing" },
  { key: "create-billing-work", label: "Create billing work" },
  { key: "prepare-billing", label: "Prepare / post" },
  { key: "process-manual-ibais", label: "Manual IBAIS" }
];

const STEP_ORDER = [
  "link-broker",
  "link-for-billing",
  "create-billing-work",
  "prepare-billing",
  "process-manual-ibais"
];

export default {
  name: "WorkbenchV2",
  data() {
    return {
      loading: false,
      error: null,
      rows: [],
      selected: null,
      searchInput: "",
      q: "",
      nextStepFilter: "all-open",
      bookToolsOpen: false,
      actionBusy: false,
      actionError: null,
      filters: FILTERS,
      brokerStatus: null,
      brokerSearch: "",
      brokerCandidates: [],
      pickedBrokerId: null
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return "Loading…";
      return `${this.rows.length} matching`;
    },
    currentStepKey() {
      if (!this.selected) return null;
      const key = this.selected.nextStepKey;
      if (key === "review-and-post") return "prepare-billing";
      if (STEP_ORDER.indexOf(key) >= 0) return key;
      return null;
    },
    billingSteps() {
      const cur = this.currentStepKey;
      const isManual = cur === "process-manual-ibais";
      // Fuse lane: broker → Insight client → mint → prepare. Manual lane skips Insight client + prepare.
      const order = isManual
        ? ["link-broker", "create-billing-work", "process-manual-ibais"]
        : ["link-broker", "link-for-billing", "create-billing-work", "prepare-billing"];
      const curIdx = cur ? order.indexOf(cur) : -1;
      const labels = {
        "link-broker": "Link broker",
        "link-for-billing": "Link for billing",
        "create-billing-work": "Create billing work",
        "prepare-billing": "Prepare",
        "process-manual-ibais": "Manual IBAIS"
      };
      return order.map((key, i) => ({
        key,
        n: i + 1,
        label: labels[key],
        done: curIdx >= 0 ? i < curIdx : false,
        current: key === cur
      }));
    },
    actionNote() {
      if (!this.selected) return "";
      if (this.selected.nextStepKey === "link-broker") {
        return "Link the producing broker to INSIGHT before creating the billing client.";
      }
      if (this.selected.nextStepKey === "create-billing-work") {
        return "Bound for this period. No billable ledger row yet.";
      }
      if (this.selected.nextStepKey === "link-for-billing") {
        return "Broker is linked. Create or adopt the INSIGHT client next.";
      }
      if (this.selected.nextStepKey === "prepare-billing" || this.selected.nextStepKey === "review-and-post") {
        return "Billing ticket ready. Continue in billing processing. Post stays human.";
      }
      if (this.selected.nextStepKey === "process-manual-ibais") {
        return "Rentsure/CRI billing ticket ready. Continue Manual IBAIS on the processing page (no Fuse Stamp/Schedule/Post).";
      }
      return "";
    },
    showBrokerPicker() {
      if (!this.selected || this.selected.nextStepKey !== "link-broker") return false;
      if (!this.brokerStatus) return true;
      if (this.brokerStatus.brokerLinked) return false;
      if (this.brokerStatus.autoMatch && this.brokerStatus.autoMatch.id) return false;
      return true;
    },
    brokerConnection() {
      return (this.brokerStatus && this.brokerStatus.producingBrokerConnection) || null;
    },
    brokerEnsureGoldVisible() {
      const c = this.brokerConnection;
      if (!c || !c.zoho) return false;
      return !c.neon || (c.primaryCta && c.primaryCta.key === "ensure-broker-gold");
    },
    selectedWorkContext() {
      if (!this.selected) return {};
      return {
        transactionId: this.selected.transactionId || undefined,
        policyId: this.selected.policyId || undefined,
        rowSubagentId: this.selected.subagentId || undefined
      };
    },
    primaryAction() {
      if (!this.selected) return null;
      const key = this.selected.nextStepKey;
      if (key === "link-broker" && this.selected.clientId) {
        if (this.brokerStatus && this.brokerStatus.autoMatch && this.brokerStatus.autoMatch.id) {
          return { key: "link-broker", label: "Link broker" };
        }
        if (this.pickedBrokerId) {
          return { key: "link-broker", label: "Link selected broker" };
        }
        return { key: "link-broker", label: "Link broker" };
      }
      if (key === "create-billing-work" && this.selected.transactionId) {
        return { key: "mint", label: "Create billing work" };
      }
      if (key === "link-for-billing" && this.selected.clientId) {
        return { key: "link", label: "Link for billing" };
      }
      if (
        (key === "prepare-billing" || key === "review-and-post") &&
        this.selected.ledgerRowId
      ) {
        return {
          key: "billing",
          label: key === "review-and-post"
            ? "Review and post (new tab)"
            : "Prepare billing (new tab)"
        };
      }
      if (key === "process-manual-ibais" && this.selected.ledgerRowId) {
        return { key: "billing", label: "Process Manual IBAIS (new tab)" };
      }
      return null;
    },
    primaryDisabled() {
      if (!this.primaryAction || this.primaryAction.key !== "link-broker") return false;
      if (this.brokerStatus && this.brokerStatus.autoMatch && this.brokerStatus.autoMatch.id) return false;
      if (!this.showBrokerPicker) return false;
      return !this.pickedBrokerId;
    }
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.syncFromRoute();
        this.loadWork();
      }
    },
    selected: {
      handler(row) {
        this.resetBrokerUi();
        if (row && row.nextStepKey === "link-broker" && row.clientId) {
          this.loadBrokerStatus(row);
        }
      }
    }
  },
  methods: {
    syncFromRoute() {
      const q = this.$route.query.q;
      this.q = typeof q === "string" ? q : "";
      this.searchInput = this.q;
      const step = this.$route.query.nextStep;
      this.nextStepFilter = typeof step === "string" && step ? step : "all-open";
    },
    setFilter(key) {
      this.nextStepFilter = key;
      const query = { ...this.$route.query };
      if (key === "all-open") delete query.nextStep;
      else query.nextStep = key;
      this.$router.replace({ path: "/workbench-v2", query }).catch(() => {});
    },
    runSearch() {
      const query = { ...this.$route.query };
      const q = String(this.searchInput || "").trim();
      if (q) query.q = q;
      else delete query.q;
      this.$router.replace({ path: "/workbench-v2", query }).catch(() => {});
    },
    resetBrokerUi() {
      this.brokerStatus = null;
      this.brokerSearch = "";
      this.brokerCandidates = [];
      this.pickedBrokerId = null;
      this.actionError = null;
    },
    async loadBrokerStatus(row) {
      try {
        const status = await getBrokerLinkStatus(row.clientId, {
          transactionId: row.transactionId || undefined,
          rowSubagentId: row.subagentId || undefined,
          policyId: row.policyId || undefined
        });
        this.brokerStatus = status || null;
        if (status && Array.isArray(status.candidates) && status.candidates.length) {
          this.brokerCandidates = status.candidates.filter(c => c && c.id);
          if (this.brokerCandidates.length === 1) {
            this.pickedBrokerId = this.brokerCandidates[0].id;
          }
        }
        if (status && status.subagentName && !this.brokerSearch) {
          this.brokerSearch = status.subagentName;
        }
      } catch (e) {
        this.brokerStatus = null;
        this.actionError = this.apiError(e, "Unable to load broker link status.");
      }
    },
    async runBrokerSearch() {
      this.actionError = null;
      this.actionBusy = true;
      try {
        const result = await searchInsightProducingBrokers(this.brokerSearch);
        if (result && result.error) {
          this.actionError = result.error;
          this.brokerCandidates = [];
          return;
        }
        const rows = Array.isArray(result && result.results) ? result.results : [];
        this.brokerCandidates = rows.map(r => ({
          id: r.id || r.insightBrokerId,
          name: r.name
        })).filter(r => r.id && r.name);
        if (this.brokerCandidates.length === 1) {
          this.pickedBrokerId = this.brokerCandidates[0].id;
        }
      } catch (e) {
        this.actionError = this.apiError(e, "Unable to search producing brokers.");
      } finally {
        this.actionBusy = false;
      }
    },
    async loadWork() {
      this.loading = true;
      this.error = null;
      this.actionError = null;
      try {
        const opts = { q: this.q || undefined };
        if (this.nextStepFilter === "prepare-billing") {
          opts.nextStep = undefined;
        } else if (this.nextStepFilter !== "all-open") {
          opts.nextStep = this.nextStepFilter;
        }
        let rows = await getWorkbenchWork(opts);
        rows = Array.isArray(rows) ? rows : [];
        if (this.nextStepFilter === "prepare-billing") {
          rows = rows.filter(
            r =>
              r.nextStepKey === "prepare-billing" ||
              r.nextStepKey === "review-and-post" ||
              r.nextStepKey === "process-manual-ibais"
          );
        }
        this.rows = rows;
        if (this.selected) {
          this.selected = rows.find(r => r.id === this.selected.id) || rows[0] || null;
        } else {
          this.selected = rows[0] || null;
        }
      } catch (e) {
        this.error = "Unable to load work right now.";
        this.rows = [];
        this.selected = null;
      } finally {
        this.loading = false;
      }
    },
    rowLine(row) {
      const bits = [row.policyNumber, row.transactionName, row.ledgerRowId ? `ledger-${row.ledgerRowId}` : ""]
        .filter(Boolean);
      return bits.join(" · ") || row.id;
    },
    detailRef(row) {
      return [row.policyNumber, row.transactionName || (row.transactionId ? `TR ${row.transactionId}` : "")]
        .filter(Boolean)
        .join(" · ");
    },
    formatMoney(n) {
      if (n == null || !Number.isFinite(n)) return "—";
      return `$${Math.round(n).toLocaleString("en-AU")}`;
    },
    formatDate(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return String(d);
      return dt.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "2-digit" });
    },
    formatShortDate(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
      return dt.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
    },
    periodRange(row) {
      const a = this.formatDate(row.periodStart);
      const b = this.formatDate(row.periodEnd);
      if (a === "—" && b === "—") return "—";
      return `${a} – ${b}`;
    },
    pillClass(key) {
      if (key === "link-broker") return "red";
      if (key === "create-billing-work") return "amber";
      if (key === "link-for-billing") return "red";
      if (key === "prepare-billing" || key === "review-and-post") return "blue";
      if (key === "process-manual-ibais") return "blue";
      if (key === "view-only") return "green";
      return "amber";
    },
    /**
     * Leave Workbench open as the place-marker; open the next surface in a new tab.
     * In-page actions (link broker / link for billing / ensure gold) stay here.
     */
    openInNewTab(location) {
      if (!location) return;
      const resolved = this.$router.resolve(location);
      const href = resolved && resolved.href ? resolved.href : null;
      if (!href) return;
      window.open(href, "_blank", "noopener,noreferrer");
    },
    openClient(clientId) {
      const query = {};
      if (this.selected) {
        if (this.selected.transactionId) query.transactionId = this.selected.transactionId;
        if (this.selected.policyId) query.policyId = this.selected.policyId;
        if (this.selected.subagentId) query.rowSubagentId = this.selected.subagentId;
      }
      this.openInNewTab({ path: `/workbench/clients/${clientId}`, query });
    },
    apiError(e, fallback) {
      const data = e && e.response && e.response.data;
      const detail = data && (data.error || data.message || data.result);
      return detail ? String(detail) : fallback;
    },
    async runPrimary() {
      if (!this.primaryAction || !this.selected) return;
      this.actionError = null;
      if (this.primaryAction.key === "billing") {
        this.openInNewTab({
          path: `/billing-processing/ledger-${this.selected.ledgerRowId}`
        });
        return;
      }
      if (this.primaryAction.key === "link-broker") {
        await this.doLinkBroker();
        return;
      }
      if (this.primaryAction.key === "link") {
        await this.doLinkForBilling();
        return;
      }
      if (this.primaryAction.key === "mint") {
        this.actionBusy = true;
        try {
          const result = await createBillingWork(this.selected.transactionId);
          if (result && result.error) {
            this.actionError = result.error;
            return;
          }
          if (result && result.ledgerRowId) {
            // Refresh the queue in this tab, then open billing beside it.
            await this.loadWork();
            this.openInNewTab({
              path: `/billing-processing/ledger-${result.ledgerRowId}`
            });
            return;
          }
          this.actionError = "Billing ticket was not created.";
        } catch (e) {
          this.actionError = this.apiError(e, "Unable to create billing work right now.");
        } finally {
          this.actionBusy = false;
        }
      }
    },
    async doLinkBroker() {
      this.actionBusy = true;
      this.actionError = null;
      try {
        const body = { ...this.selectedWorkContext };
        if (this.pickedBrokerId) {
          body.insightBrokerId = this.pickedBrokerId;
        } else if (this.brokerStatus && this.brokerStatus.autoMatch && this.brokerStatus.autoMatch.id) {
          body.insightBrokerId = this.brokerStatus.autoMatch.id;
        }
        const result = await linkClientBroker(this.selected.clientId, body);
        if (result && result.needsPick) {
          this.brokerCandidates = (result.candidates || []).filter(c => c && c.id);
          this.actionError = result.error || "Pick the matching INSIGHT producing broker, then link.";
          return;
        }
        if (result && result.error) {
          this.actionError = result.error;
          if (Array.isArray(result.candidates)) {
            this.brokerCandidates = result.candidates.filter(c => c && c.id);
          }
          return;
        }
        await this.loadWork();
        if (this.selected && this.selected.nextStepKey === "link-broker") {
          await this.loadBrokerStatus(this.selected);
        }
      } catch (e) {
        this.actionError = this.apiError(e, "Unable to link the producing broker right now.");
      } finally {
        this.actionBusy = false;
      }
    },
    async doEnsureBrokerGold() {
      if (!this.selected || !this.selected.clientId) return;
      if (!window.confirm("Create or adopt the Neon Gold party for this producing broker and stamp it on Zoho?")) {
        return;
      }
      this.actionBusy = true;
      this.actionError = null;
      try {
        const result = await ensureBrokerGold(this.selected.clientId, {
          ...this.selectedWorkContext,
          confirmed: true
        });
        if (result && result.error) {
          this.actionError = result.error;
          return;
        }
        await this.loadBrokerStatus(this.selected);
      } catch (e) {
        this.actionError = this.apiError(e, "Unable to ensure the producing broker Gold party right now.");
      } finally {
        this.actionBusy = false;
      }
    },
    async doLinkForBilling() {
      this.actionBusy = true;
      this.actionError = null;
      try {
        const result = await fixClientLink(this.selected.clientId, {
          ...this.selectedWorkContext,
          brand: this.selected.brand || undefined
        });
        if (result && result.needsGold) {
          if (window.confirm((result.error || "This client needs a Gold party first.") + "\n\nTry Ensure Gold party now?")) {
            const gold = await ensureClientGold(this.selected.clientId, {
              ...this.selectedWorkContext,
              confirmed: true
            });
            if (!gold) {
              this.actionError = "Ensure Gold party returned no result. Try again.";
              return;
            }
            if (gold.error) {
              this.actionError = gold.error;
              return;
            }
            if (!gold.done && !gold.partyRef) {
              this.actionError = gold.detail
                || "Ensure Gold party did not adopt a Neon party. No change was made.";
              return;
            }
            const retry = await fixClientLink(this.selected.clientId, {
              ...this.selectedWorkContext,
              brand: this.selected.brand || undefined
            });
            if (retry && retry.error) {
              this.actionError = retry.error;
              return;
            }
            await this.loadWork();
            return;
          }
          this.actionError = result.error
            || "This client needs a Gold party first. Use Ensure Gold party, then Link for billing.";
          return;
        }
        if (result && result.error) {
          this.actionError = result.error;
          return;
        }
        await this.loadWork();
      } catch (e) {
        this.actionError = this.apiError(e, "Unable to fix the client link right now.");
      } finally {
        this.actionBusy = false;
      }
    }
  }
};
</script>

<style scoped>
.wb2 {
  --bg: #ffffff;
  --subtle: #f8fafc;
  --raised: #f1f5f9;
  --line: #e5e7eb;
  --ink: #111827;
  --secondary: #4b5563;
  --muted: #6b7280;
  --faint: #9ca3af;
  --accent: #4f46e5;
  --accent-soft: #eef2ff;
  --green: #16a34a;
  --green-soft: #dcfce7;
  --blue: #2563eb;
  --blue-soft: #eff6ff;
  --red: #dc2626;
  --red-soft: #fee2e2;
  --amber: #b45309;
  --amber-soft: #fef3c7;
  min-height: calc(100vh - 120px);
  background: var(--bg);
  color: var(--ink);
  display: flex;
  flex-direction: column;
}
.wb2-top {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--line);
}
.wb2-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 14px;
}
.wb2-mark {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: var(--accent);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}
.wb2-search {
  flex: 1;
  max-width: 480px;
  display: flex;
  gap: 8px;
}
.wb2-search input {
  flex: 1;
  border: 1px solid var(--line);
  background: var(--subtle);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
}
.wb2-top-actions {
  display: flex;
  gap: 8px;
}
.wb2-body {
  display: flex;
  flex: 1;
  min-height: 0;
}
.wb2-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.wb2-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid var(--line);
}
.wb2-title {
  font-size: 15px;
  font-weight: 600;
}
.wb2-sub {
  margin-left: 10px;
  font-size: 13px;
  color: var(--muted);
}
.wb2-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 20px;
  border-bottom: 1px solid var(--line);
  background: var(--subtle);
}
.wb2-chip {
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 9999px;
  border: 1px solid var(--line);
  background: var(--bg);
  color: var(--secondary);
}
.wb2-chip.on {
  background: var(--accent-soft);
  border-color: transparent;
  color: var(--accent);
  font-weight: 600;
}
.wb2-cols,
.wb2-row {
  display: grid;
  grid-template-columns: 1.4fr 110px 150px 100px 160px 90px;
  gap: 12px;
  align-items: center;
  padding: 10px 20px;
  text-align: left;
}
.wb2-cols {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
  border-bottom: 1px solid var(--line);
}
.wb2-row {
  border: 0;
  border-bottom: 1px solid var(--raised);
  border-left: 2px solid transparent;
  background: transparent;
  font-size: 13px;
  color: inherit;
  width: 100%;
  cursor: pointer;
}
.wb2-row.sel {
  background: var(--subtle);
  border-left-color: var(--accent);
}
.wb2-row .n {
  font-weight: 600;
}
.wb2-row .m,
.mono.m {
  font-size: 12px;
  color: var(--muted);
}
.mono {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 12px;
}
.wb2-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 4px 9px;
  font-size: 11px;
  font-weight: 600;
}
.wb2-pill.amber {
  background: var(--amber-soft);
  color: var(--amber);
}
.wb2-pill.blue {
  background: var(--blue-soft);
  color: var(--blue);
}
.wb2-pill.red {
  background: var(--red-soft);
  color: var(--red);
}
.wb2-pill.green {
  background: var(--green-soft);
  color: var(--green);
}
.wb2-detail {
  width: 360px;
  border-left: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  background: var(--bg);
}
.wb2-d-head {
  padding: 18px 18px 14px;
  border-bottom: 1px solid var(--line);
}
.label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}
.d-name {
  font-size: 17px;
  font-weight: 600;
  margin: 8px 0 4px;
}
.wb2-facts {
  padding: 8px 18px;
}
.fact {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid var(--raised);
  font-size: 13px;
}
.fact .k {
  color: var(--muted);
}
.fact .v {
  font-weight: 500;
  text-align: right;
}
.wb2-note {
  margin: 12px 18px;
  padding: 10px 12px;
  background: var(--amber-soft);
  border-radius: 6px;
  font-size: 13px;
  color: var(--amber);
  line-height: 1.4;
}
.wb2-note.ok {
  background: var(--blue-soft);
  color: var(--blue);
}
.wb2-steps {
  padding: 12px 18px 4px;
}
.wb2-steps ol {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}
.wb2-steps li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  font-size: 13px;
  color: var(--muted);
}
.wb2-steps li.done {
  color: var(--green);
}
.wb2-steps li.current {
  color: var(--ink);
  font-weight: 600;
}
.wb2-steps .mark {
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  border: 1px solid var(--line);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}
.wb2-steps li.current .mark {
  border-color: var(--accent);
  background: var(--accent-soft);
  color: var(--accent);
}
.wb2-steps li.done .mark {
  border-color: transparent;
  background: var(--green-soft);
  color: var(--green);
}
.wb2-conn {
  margin: 0 18px 10px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--subtle);
  font-size: 12px;
}
.wb2-conn .label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
  font-weight: 700;
  margin-bottom: 6px;
}
.wb2-conn .leg {
  color: var(--ink);
  margin-bottom: 3px;
}
.wb2-conn .miss {
  color: var(--amber);
}
.wb2-conn .wb2-btn-s {
  margin-top: 8px;
}
.wb2-broker-line {
  margin: 0 18px 8px;
  font-size: 12px;
  color: var(--secondary);
}
.wb2-broker-pick {
  margin: 0 18px 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.wb2-broker-pick input {
  border: 1px solid var(--line);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
}
.wb2-candidate {
  text-align: left;
  border: 1px solid var(--line);
  background: var(--subtle);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  cursor: pointer;
}
.wb2-candidate.on {
  border-color: var(--accent);
  background: var(--accent-soft);
}
.wb2-actions {
  margin-top: auto;
  padding: 16px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.wb2-btn-p,
.wb2-btn-s {
  text-align: center;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  text-decoration: none;
  display: inline-block;
}
.wb2-btn-p {
  background: var(--accent);
  color: #fff;
  border: 0;
}
.wb2-btn-s {
  background: var(--bg);
  border-color: var(--line);
  color: var(--ink);
}
.wb2-btn-p:disabled,
.wb2-btn-s:disabled {
  opacity: 0.55;
}
.wb2-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px;
  color: var(--muted);
  text-align: center;
}
.wb2-empty h2 {
  margin: 0;
  font-size: 16px;
  color: var(--ink);
}
.wb2-error {
  margin: 12px 20px;
  padding: 10px 12px;
  background: var(--red-soft);
  color: var(--red);
  border-radius: 6px;
  font-size: 13px;
}
.wb2-drawer-mask {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.2);
  z-index: 40;
}
.wb2-drawer {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 320px;
  background: var(--bg);
  border-left: 1px solid var(--line);
  padding: 18px;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.06);
}
.wb2-drawer h3 {
  margin: 0 0 6px;
  font-size: 15px;
}
.wb2-drawer p {
  margin: 0 0 14px;
  font-size: 13px;
  color: var(--muted);
}
.wb2-tool {
  display: block;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  text-decoration: none;
}
</style>
